import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  BookOpen, Download, ExternalLink, AlertCircle, Shield, Clock,
  CheckCircle, ChevronLeft, FileText
} from 'lucide-react';
import { INTEGRATION_GUIDES, LmsType } from '../data/integrationGuides';
import { authenticatedFetch } from '../lib/auth';

function renderBody(body: string) {
  // Simple markdown-ish rendering: split on newlines, handle **bold**, `code`, and lists.
  const lines = body.split('\n');
  return lines.map((line, i) => {
    if (line.trim() === '') return <br key={i} />;
    const indented = line.startsWith('   ') || line.startsWith('  -');
    const content = line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+?)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-sm font-mono">$1</code>');
    return (
      <p key={i} className={`${indented ? 'ml-4' : ''} mb-1`} dangerouslySetInnerHTML={{ __html: content }} />
    );
  });
}

export default function LmsIntegrationGuidePage() {
  const { lmsType } = useParams<{ lmsType?: string }>();
  const navigate = useNavigate();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // If no lmsType, show index
  if (!lmsType || !(lmsType in INTEGRATION_GUIDES)) {
    return <GuideIndexPage />;
  }

  const guide = INTEGRATION_GUIDES[lmsType as LmsType];

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const r = await authenticatedFetch(`/api/integration-guides/${guide.lmsType}/pdf`);
      if (!r.ok) throw new Error('Kunne ikke generere PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kompetanseattest-${guide.displayName}-veiledning.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Kunne ikke laste ned PDF: ' + err.message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" />
        Tilbake
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <BookOpen className="h-4 w-4" />
              <span>LMS-integrasjon</span>
              <span>·</span>
              <Link to="/help/integrations" className="hover:underline">Alle veiledninger</Link>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{guide.displayName}</h1>
            <p className="text-gray-600 mt-1">{guide.tagline}</p>
          </div>
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary/90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {downloadingPdf ? 'Genererer...' : 'Last ned som PDF'}
          </button>
        </div>

        <p className="mt-4 text-gray-700">{guide.description}</p>

        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Ca. {guide.estimatedTime}
          </span>
        </div>
      </div>

      {/* Prerequisites */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          Forutsetninger
        </h2>
        <ul className="space-y-1 text-gray-700">
          {guide.prerequisites.map((p, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-gray-400 mt-1">•</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Steps */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Steg-for-steg-oppsett</h2>
        <div className="space-y-4">
          {guide.steps.map((step, i) => (
            <div key={i} className="border-l-4 border-kf-primary pl-4 pb-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-kf-primary text-white text-sm mr-2">
                  {i + 1}
                </span>
                {step.title}
              </h3>
              <div className="text-gray-700 ml-9">
                {renderBody(step.body)}
                {step.note && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                    <strong>Merk:</strong> {step.note}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Required functions (Moodle only) */}
      {guide.requiredFunctions && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">API-funksjoner som må legges til</h2>
          <div className="space-y-2">
            {guide.requiredFunctions.filter(f => f.required).length > 0 && (
              <>
                <p className="text-sm font-medium text-red-700 mb-1">🔴 Påkrevde:</p>
                <ul className="space-y-1 ml-4">
                  {guide.requiredFunctions.filter(f => f.required).map(f => (
                    <li key={f.name} className="text-sm">
                      <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">{f.name}</code>
                      <span className="text-gray-600 ml-2">— {f.description}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {guide.requiredFunctions.filter(f => !f.required).length > 0 && (
              <>
                <p className="text-sm font-medium text-amber-700 mt-4 mb-1">🟡 Anbefalt:</p>
                <ul className="space-y-1 ml-4">
                  {guide.requiredFunctions.filter(f => !f.required).map(f => (
                    <li key={f.name} className="text-sm">
                      <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">{f.name}</code>
                      <span className="text-gray-600 ml-2">— {f.description}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Common errors */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          Vanlige feil og løsninger
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-600 text-xs uppercase tracking-wide">
                <th className="py-2 pr-4">Feilmelding</th>
                <th className="py-2 pr-4">Årsak</th>
                <th className="py-2">Løsning</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {guide.commonErrors.map((err, i) => (
                <tr key={i}>
                  <td className="py-2 pr-4 font-mono text-xs">{err.error}</td>
                  <td className="py-2 pr-4 text-gray-700">{err.cause}</td>
                  <td className="py-2 text-gray-900">{err.solution}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security tips */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-kf-blue-600" />
          Sikkerhet og beste praksis
        </h2>
        <ul className="space-y-2 text-gray-700">
          {guide.securityTips.map((tip, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-kf-blue-400 mt-1">•</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Official docs */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
        <ExternalLink className="h-4 w-4 inline mr-1" />
        <a
          href={guide.officialDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-kf-primary underline"
        >
          {guide.officialDocsLabel}
        </a>
      </div>
    </div>
  );
}

function GuideIndexPage() {
  const guides = Object.values(INTEGRATION_GUIDES);
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-3xl font-bold text-gray-900">Veiledninger for LMS-integrasjon</h1>
        <p className="text-gray-600 mt-2">
          Detaljerte steg-for-steg-veiledninger for hvordan du setter opp integrasjoner mellom
          Kompetanseattest og de ulike LMS-systemene. Alle veiledninger kan lastes ned som PDF for
          deling internt eller med tekniske samarbeidspartnere.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {guides.map(g => (
          <Link
            key={g.lmsType}
            to={`/help/integrations/${g.lmsType}`}
            className="bg-white rounded-lg shadow-sm border p-5 hover:border-kf-primary hover:shadow-md transition"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{g.displayName}</h2>
                <p className="text-sm text-gray-600 mt-1">{g.tagline}</p>
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Ca. {g.estimatedTime}
                </p>
              </div>
              <FileText className="h-8 w-8 text-gray-300" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
